/**
 * Lightweight personalization for Explore.
 * Tracks clicked tags and item IDs in localStorage.
 * Never "creepy" — just uses history to surface a "Utvalt för dig" section.
 *
 * See docs/design-rules/ux-rules/explore-12-10-sot.md §10
 */

const HISTORY_KEY = "explore_history";
const MAX_TAGS = 50;
const MAX_IDS = 50;
/** 30 days TTL */
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

interface ExploreHistory {
  /** Recently clicked tags/badges */
  tags: string[];
  /** Recently viewed item IDs */
  ids: string[];
  /** Last updated timestamp */
  ts: number;
}

function isExpired(ts: number): boolean {
  return Date.now() - ts > TTL_MS;
}

function readHistory(): ExploreHistory {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return { tags: [], ids: [], ts: 0 };
    const parsed = JSON.parse(raw);
    const h: ExploreHistory = {
      tags: Array.isArray(parsed.tags) ? parsed.tags : [],
      ids: Array.isArray(parsed.ids) ? parsed.ids : [],
      ts: parsed.ts || 0,
    };
    // Prune if expired
    if (h.ts > 0 && isExpired(h.ts)) {
      clearExploreHistory();
      return { tags: [], ids: [], ts: 0 };
    }
    // Enforce limits on read (defensive)
    h.tags = h.tags.slice(0, MAX_TAGS);
    h.ids = h.ids.slice(0, MAX_IDS);
    return h;
  } catch {
    return { tags: [], ids: [], ts: 0 };
  }
}

function writeHistory(h: ExploreHistory) {
  try {
    // Enforce limits before write
    h.tags = h.tags.slice(0, MAX_TAGS);
    h.ids = h.ids.slice(0, MAX_IDS);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(h));
  } catch {
    // ignore
  }
}

/** Record a clicked item's tags and ID */
export function recordExploreClick(id: string, badges: string[]) {
  const h = readHistory();

  // Add ID (dedupe, keep recent first)
  h.ids = [id, ...h.ids.filter((x) => x !== id)].slice(0, MAX_IDS);

  // Add tags (dedupe, keep recent first)
  const newTags = badges.map((b) => b.toLowerCase());
  h.tags = [...newTags, ...h.tags.filter((t) => !newTags.includes(t))].slice(0, MAX_TAGS);

  h.ts = Date.now();
  writeHistory(h);
}

/** Get curated tags and IDs for personalization */
export function getExploreHistory(): { tags: string[]; ids: Set<string> } {
  const h = readHistory();
  return {
    tags: h.tags,
    ids: new Set(h.ids),
  };
}

/** Clear all personalization data */
export function clearExploreHistory() {
  try {
    localStorage.removeItem(HISTORY_KEY);
  } catch {
    // ignore
  }
}
